import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordenesApi } from '../services/api';
import { useSucursal } from '../context/SucursalContext';
import { useAuth } from '../context/AuthContext';

const OrdenesLista = () => {
  const { getSucursalId, getSucursalNombre } = useSucursal();
  const { user } = useAuth();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    loadOrdenes();
  }, [statusFilter, getSucursalId()]);

  const loadOrdenes = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;

      const tienda_id = getSucursalId();
      if (tienda_id) params.tienda_id = tienda_id;

      // Si es técnico, solo mostrar sus órdenes asignadas
      if (user && user.rol === 'tecnico') {
        params.tecnico_id = user.id;
      }

      const response = await ordenesApi.getAll(params);
      setOrdenes(response.data || []);
      setPagination(response.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error cargando órdenes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchTerm.length < 2) {
      loadOrdenes();
      return;
    }
    setLoading(true);
    try {
      // Pasar tecnico_id si es técnico para filtrar en el backend
      const extraParams = {};
      if (user && user.rol === 'tecnico') {
        extraParams.tecnico_id = user.id;
      }
      const response = await ordenesApi.search(searchTerm, extraParams);
      // Filtrar por sucursal si está seleccionada
      let data = response.data || [];
      const tienda_id = getSucursalId();
      if (tienda_id) {
        data = data.filter(o => o.tienda_id === tienda_id);
      }
      setOrdenes(data);
      setPagination({ page: 1, pages: 1, total: data.length });
    } catch (error) {
      console.error('Error buscando:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta orden?')) return;
    try {
      await ordenesApi.delete(id);
      loadOrdenes(pagination.page);
    } catch (error) {
      alert('Error al eliminar la orden');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-GT');
  };

  const formatMoney = (amount) => {
    return `Q${parseFloat(amount || 0).toFixed(2)}`;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Órdenes de Reparación</h1>
          <p className="page-subtitle">{getSucursalNombre()} - {pagination.total} órdenes</p>
        </div>
        <Link to="/ordenes/nueva" className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nueva Orden
        </Link>
      </div>

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-input">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar por número, cliente, teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
            style={{ paddingLeft: '40px' }}
          />
        </form>

        <select
          className="form-control"
          style={{ width: 'auto', minWidth: '150px' }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="proceso">En Proceso</option>
          <option value="terminada">Terminada</option>
          <option value="entregada">Entregada</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : ordenes.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <h3>No hay órdenes</h3>
            <p>Crea una nueva orden para comenzar</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>N° Orden</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Teléfono</th>
                    <th>Equipo</th>
                    <th>Servicio</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Saldo</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenes.map(orden => (
                    <tr key={orden.id}>
                      <td>
                        <Link to={`/ordenes/${orden.id}`} style={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                          #{orden.numero_orden}
                        </Link>
                      </td>
                      <td>{formatDate(orden.fecha_recepcion)}</td>
                      <td>{orden.nombre_cliente}</td>
                      <td>{orden.telefono_cliente || '-'}</td>
                      <td>
                        <span title={orden.equipo1_marca}>
                          {orden.equipo1_descripcion?.substring(0, 30) || '-'}
                          {orden.equipo1_descripcion?.length > 30 ? '...' : ''}
                        </span>
                      </td>
                      <td>{orden.tipo_servicio}</td>
                      <td>
                        <span className={`status-badge status-${orden.status}`}>
                          {orden.status}
                        </span>
                      </td>
                      <td style={{ fontWeight: 500 }}>{formatMoney(orden.valor_total)}</td>
                      <td style={{ color: parseFloat(orden.saldo) > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
                        {formatMoney(orden.saldo)}
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link to={`/ordenes/${orden.id}`} className="btn btn-secondary btn-icon" title="Ver">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </Link>
                          <Link to={`/ordenes/editar/${orden.id}`} className="btn btn-secondary btn-icon" title="Editar">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </Link>
                          <button
                            className="btn btn-danger btn-icon"
                            onClick={() => handleDelete(orden.id)}
                            title="Eliminar"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`btn ${page === pagination.page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                    onClick={() => loadOrdenes(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrdenesLista;
